// v1.9 P0: Input 原子组件
// label / error / hint / leftIcon / rightIcon / size / block
// 全部 mobile-first (default) + sm+ 字号
//
// a11y:
// - label 通过 htmlFor 绑 id
// - error 时 aria-invalid=true + aria-describedby
// - hint 通过 aria-describedby 关联
import { type InputHTMLAttributes, forwardRef, useId } from 'react';
import { twMerge } from 'tailwind-merge';
import { Icon } from '../Icon';

export type InputSize = 'sm' | 'md' | 'lg';

export interface InputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size'> {
  /** 标签 (field label) */
  label?: string;
  /** 错误信息 (会显示红色 + aria-invalid) */
  error?: string;
  /** 提示文字 (灰色, 在 input 下方) */
  hint?: string;
  /** 左侧 icon (Icon name) */
  leftIcon?: string;
  /** 右侧 icon (Icon name) */
  rightIcon?: string;
  size?: InputSize;
  /** 全宽 */
  block?: boolean;
  /** 必填红星 */
  required?: boolean;
  /** 容器 className (会包整个 label+input+hint) */
  containerClassName?: string;
}

const SIZE_CLASS: Record<InputSize, string> = {
  sm: 'px-2.5 py-1.5 text-xs',
  md: 'px-3 py-2 text-sm',
  lg: 'px-3.5 py-2.5 text-base',
};

const ICON_LEFT_PADDING: Record<InputSize, string> = {
  sm: 'pl-7',
  md: 'pl-9',
  lg: 'pl-11',
};

const ICON_RIGHT_PADDING: Record<InputSize, string> = {
  sm: 'pr-7',
  md: 'pr-9',
  lg: 'pr-11',
};

// Icon size mapping kept for future use; rightIcon/leftIcon sizes are derived
// inline from `size` prop to avoid an extra lookup table.
void (null as unknown as InputSize);

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  {
    label,
    error,
    hint,
    leftIcon,
    rightIcon,
    size = 'md',
    block = false,
    required,
    containerClassName,
    id: idProp,
    className,
    disabled,
    ...rest
  },
  ref,
) {
  const autoId = useId();
  const id = idProp ?? autoId;
  const errorId = error ? `${id}-err` : undefined;
  const hintId = hint ? `${id}-hint` : undefined;
  const describedBy = [errorId, hintId].filter(Boolean).join(' ') || undefined;

  return (
    <div className={twMerge(block && 'w-full', containerClassName)}>
      {label && (
        <label htmlFor={id} className="block text-xs text-ink-2 mb-1.5 select-none">
          {label}
          {required && (
            <span className="text-bad ml-0.5" aria-hidden>
              *
            </span>
          )}
        </label>
      )}
      <div className="relative">
        {leftIcon && (
          <span
            className="absolute left-2.5 top-1/2 -translate-y-1/2 text-ink-3 pointer-events-none"
            aria-hidden
          >
            <Icon name={leftIcon} size={size === 'lg' ? 'md' : 'sm'} />
          </span>
        )}
        <input
          ref={ref}
          id={id}
          disabled={disabled}
          aria-invalid={error ? 'true' : undefined}
          aria-describedby={describedBy}
          aria-required={required || undefined}
          className={twMerge(
            'w-full bg-surface border rounded-md text-ink',
            'placeholder:text-ink-3',
            'transition-colors duration-150',
            'focus:outline-none focus:border-accent focus:ring-2 focus:ring-accent/15',
            'disabled:bg-surface-2 disabled:text-ink-3 disabled:cursor-not-allowed',
            error
              ? 'border-bad focus:border-bad focus:ring-bad/15'
              : 'border-line hover:border-ink-3',
            SIZE_CLASS[size],
            leftIcon && ICON_LEFT_PADDING[size],
            rightIcon && ICON_RIGHT_PADDING[size],
            className,
          )}
          {...rest}
        />
        {rightIcon && (
          <span
            className="absolute right-2.5 top-1/2 -translate-y-1/2 text-ink-3 pointer-events-none"
            aria-hidden
          >
            <Icon name={rightIcon} size={size === 'lg' ? 'md' : 'sm'} />
          </span>
        )}
      </div>
      {error && (
        <p id={errorId} role="alert" className="text-xs text-bad mt-1 flex items-center gap-1">
          <Icon name="alert-circle" size="xs" aria-hidden />
          {error}
        </p>
      )}
      {hint && !error && (
        <p id={hintId} className="text-xs text-ink-3 mt-1">
          {hint}
        </p>
      )}
    </div>
  );
});

// (See ICON_SIZE note above.)
